import React, { useRef, useState } from 'react';
import { Tag, Button, Input, InputNumber, Space, Typography } from 'antd';
import { RobotOutlined, ExclamationCircleOutlined, CheckCircleOutlined, TrophyOutlined, QuestionCircleOutlined, CommentOutlined } from '@ant-design/icons';
import { QuestionResult, QuestionFlag } from '../services/adminService';

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Shared review-surface pieces for an AI evaluation draft, used by both
 * TrainerLedView.tsx (individual "Draft with AI" modal) and
 * BulkEvaluationBoard.tsx (per-row bulk review) - see
 * specs/2026-09-21-shared-ai-draft-review-component.md.
 *
 * Both flows already call the exact same backend
 * (adminService.ts's draftEvaluationWithAI -> /api/evaluateSubmission), so
 * the data was never forked - only this presentation was, independently
 * hand-built twice. That duplication already caused real drift (the weight
 * tag had to be removed from both by hand; "Needs Trainer Attention" was
 * built once and initially missed the other). This file is the single
 * source going forward for exactly the part that's identical between the
 * two flows: what happens once a draft exists. Everything that's genuinely
 * different (submission editing) stays in each caller - not touched here.
 */

export const CONFIDENCE_TAG_COLOR: Record<string, string> = { high: 'green', medium: 'gold', low: 'red' };
export const needsReviewCue = (confidence?: 'high' | 'medium' | 'low') => confidence === 'low';

export type { QuestionResult };

// Per-question verdicts (specs/2026-10-16-ai-eval-question-focus.md). The
// first four describe the answer; cannot_assess is the only "AI is unsure"
// signal and is worded so a trainer can't mistake it for a weak answer.
const VERDICT_DISPLAY: Record<QuestionResult['verdict'], { label: string; color: string }> = {
    met: { label: 'Met', color: 'green' },
    partial: { label: 'Partial', color: 'gold' },
    not_met: { label: 'Not met', color: 'red' },
    missing: { label: 'Missing', color: 'red' },
    cannot_assess: { label: "AI couldn't check this", color: 'default' },
};

// "Q2" -> 2, so rows follow the assignment's own order whatever order the AI
// returned them in. Anything without a number goes last, in original order.
const questionNumber = (q: QuestionResult) => {
    const n = parseInt(q.no.replace(/\D+/g, ''), 10);
    return Number.isNaN(n) ? Infinity : n;
};
const sortByAssignmentOrder = (results: QuestionResult[]) =>
    results
        .map((q, index) => ({ q, index }))
        .sort((a, b) => (questionNumber(a.q) - questionNumber(b.q)) || (a.index - b.index))
        .map(({ q }) => q);

const questionLabel = (q: QuestionResult) => (q.title ? `${q.no} ${q.title}` : q.no);

// Facilitator follow-ups on an answer - these replaced the old evaluation
// criteria (specs/2026-10-16-ai-eval-question-focus.md, criteria amendment).
const FLAG_LABEL: Record<QuestionFlag['type'], string> = {
    unclear_concept: 'Concept unclear',
    disagrees: 'Disagrees',
    open_question: 'Has a question',
    challenge_thinking: 'Challenge thinking',
};

// A question needs the trainer when the answer falls short OR there's
// something to follow up on - a well-answered question with an open
// question from the participant still needs a reply.
const needsFocus = (q: QuestionResult) => q.verdict !== 'met' || (q.flags?.length ?? 0) > 0;

export interface AiEvaluationSummaryProps {
    evaluation: string;
    onEvaluationChange: (text: string) => void;
    overallConfidence?: 'high' | 'medium' | 'low';
    questionResults?: QuestionResult[];
    possiblyAiWritten?: { reason: string };
    score: number;
    maxCredits: number;
    // Renders an editable "Credits" field. Callers that already have their own
    // editable total control elsewhere (e.g. TrainerLedView.tsx's modal
    // footer) simply omit this.
    onScoreChange?: (score: number) => void;
}

interface QuestionRowProps {
    result: QuestionResult;
    onAddComment: (result: QuestionResult) => void;
}

// One assignment question: number + title, verdict, the AI's reason, and the
// participant's own words it's based on - so the trainer can check the call
// without re-reading the whole submission.
const QuestionRow: React.FC<QuestionRowProps> = ({ result, onAddComment }) => {
    const display = VERDICT_DISPLAY[result.verdict];
    return (
        <div
            data-question-row
            style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 8px', background: '#fff', borderRadius: 6 }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Text strong style={{ fontSize: 12.5 }}>{questionLabel(result)}</Text>
                <Tag
                    color={display.color}
                    icon={result.verdict === 'cannot_assess' ? <QuestionCircleOutlined /> : undefined}
                    style={{ fontSize: 11, margin: 0 }}
                >
                    {display.label}
                </Tag>
                <Button
                    type="link"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={() => onAddComment(result)}
                    style={{ padding: 0, height: 'auto', fontSize: 12, marginLeft: 'auto' }}
                >
                    Add comment
                </Button>
            </div>
            {result.reason && <Text style={{ fontSize: 12.5, color: '#262626' }}>{result.reason}</Text>}
            {result.answer_excerpt && (
                <Text type="secondary" italic style={{ fontSize: 12 }}>“{result.answer_excerpt}”</Text>
            )}
            {result.flags?.map((flag, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
                    <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>{FLAG_LABEL[flag.type]}</Tag>
                    <Text style={{ fontSize: 12 }}>{flag.note}</Text>
                </div>
            ))}
        </div>
    );
};

// "Focus on these questions" (see needsFocus) plus
// the participant feedback box, as one unit. Owns the feedback textbox's ref
// internally - since both the [Add comment] handler and the textarea it
// focuses live in the same component, no ref needs to cross a component
// boundary at all.
export const AiEvaluationSummary: React.FC<AiEvaluationSummaryProps> = ({
    evaluation, onEvaluationChange, overallConfidence, questionResults, possiblyAiWritten, score, maxCredits, onScoreChange,
}) => {
    const evalTextRef = useRef<any>(null);
    const [showFine, setShowFine] = useState(false);
    const ordered = sortByAssignmentOrder(questionResults || []);
    const focus = ordered.filter(needsFocus);
    const fine = ordered.filter(q => !needsFocus(q));

    const handleAddComment = (result: QuestionResult) => {
        const label = result.title ? `${result.no} (${result.title})` : result.no;
        const separator = evaluation.trim() ? '\n' : '';
        onEvaluationChange(`${evaluation}${separator}${label}: `);
        requestAnimationFrame(() => {
            const textArea: HTMLTextAreaElement | undefined = evalTextRef.current?.resizableTextArea?.textArea;
            if (!textArea) return;
            textArea.focus();
            textArea.setSelectionRange(textArea.value.length, textArea.value.length);
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {possiblyAiWritten && (
                <div style={{ padding: '10px 14px', background: '#f9f0ff', borderRadius: 8, border: '1px solid #d3adf7' }}>
                    <Space size={6} align="start">
                        <RobotOutlined style={{ color: '#722ed1', marginTop: 3 }} />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Text strong style={{ fontSize: 13, color: '#531dab' }}>Possibly AI-written - check</Text>
                            <Text style={{ fontSize: 12.5 }}>{possiblyAiWritten.reason}</Text>
                        </div>
                    </Space>
                </div>
            )}

            {ordered.length > 0 && focus.length === 0 && (
                <div style={{ padding: '10px 14px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                    <Space size={6}>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text style={{ fontSize: 13, color: '#237804' }}>
                            {ordered.length === 1
                                ? 'The question is answered - nothing flagged'
                                : `All ${ordered.length} questions answered - nothing flagged`}
                        </Text>
                    </Space>
                </div>
            )}

            {focus.length > 0 && (
                <div style={{ padding: '12px 14px', background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
                    <Space size={6} style={{ marginBottom: 8 }}>
                        <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                        <Text strong style={{ fontSize: 13, color: '#874d00' }}>
                            Focus on these questions ({focus.length} of {ordered.length})
                        </Text>
                    </Space>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {focus.map((q, i) => (
                            <QuestionRow key={`${q.no}-${i}`} result={q} onAddComment={handleAddComment} />
                        ))}
                    </div>
                    {fine.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <Space size={6}>
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {fine.length === 1 ? '1 question looks fine' : `${fine.length} questions look fine`} ({fine.map(q => q.no).join(', ')})
                                </Text>
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                    onClick={() => setShowFine(v => !v)}
                                >
                                    {showFine ? 'Hide' : 'Show'}
                                </Button>
                            </Space>
                            {showFine && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                                    {fine.map((q, i) => (
                                        <QuestionRow key={`${q.no}-${i}`} result={q} onAddComment={handleAddComment} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div style={{ padding: '12px 14px', background: '#f0f5ff', borderRadius: 8, border: '1px solid #adc6ff' }}>
                <Space size={6} style={{ marginBottom: 8 }} wrap>
                    <CheckCircleOutlined style={{ color: '#1890ff' }} />
                    <Text strong style={{ fontSize: 13, color: '#1890ff' }}>Feedback to participant</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>(AI draft - edit before saving)</Text>
                    <Tag color="gold" style={{ fontSize: 12, fontWeight: 600 }}>
                        <TrophyOutlined /> Total: {score ?? 0} / {maxCredits}
                    </Tag>
                    {/* High confidence needs no tag; medium is a gentle nudge; low gets one
                        explicit "Needs review" cue - never a separate "confidence" label. */}
                    {overallConfidence === 'medium' && (
                        <Tag color={CONFIDENCE_TAG_COLOR.medium} style={{ fontSize: 11 }}>AI: double-check suggested</Tag>
                    )}
                    {needsReviewCue(overallConfidence) && (
                        <Tag color="volcano" icon={<ExclamationCircleOutlined />} style={{ fontSize: 11 }}>
                            Needs review
                        </Tag>
                    )}
                </Space>
                <TextArea
                    ref={evalTextRef}
                    rows={3}
                    value={evaluation}
                    onChange={e => onEvaluationChange(e.target.value)}
                    style={{ borderRadius: 6, fontSize: 13, marginBottom: onScoreChange ? 8 : 0 }}
                />
                {onScoreChange && (
                    <Space size={6} align="center" style={{ marginTop: 8 }}>
                        <Text style={{ fontSize: 13 }}>Credits:</Text>
                        <InputNumber
                            min={0}
                            max={maxCredits}
                            value={score}
                            onChange={val => onScoreChange(val ?? 0)}
                            size="small"
                            style={{ width: 72 }}
                        />
                    </Space>
                )}
            </div>
        </div>
    );
};
